import React from "react";

interface Props {
  data: any;
}

const scoreColor = (score: number) => {
  if (score >= 80) return "text-emerald-400 border-emerald-500/30 bg-emerald-500/10";
  if (score >= 60) return "text-amber-400 border-amber-500/30 bg-amber-500/10";
  return "text-rose-400 border-rose-500/30 bg-rose-500/10";
};

const AstrologyDetailsView: React.FC<Props> = ({ data }) => {
  if (!data) return null;

  const user = data?.user || {};
  const candidate = data?.candidate || {};
  const result = Array.isArray(data?.astrology_check) ? data.astrology_check[1] || {} : {};
  const gun = result?.gun_milan || {};
  const strengths: string[] = Array.isArray(result?.strengths) ? result.strengths : [];
  const challenges: string[] = Array.isArray(result?.challenges) ? result.challenges : [];
  const gunScores = gun?.gun_scores || {};
  const strictScores = gun?.gun_scores_strict || {};
  const syntaxHighlightJson = (obj: any) => {
    const json = JSON.stringify(obj, null, 2)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    return json.replace(
      /("(\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"(\s*:)?|\btrue\b|\bfalse\b|\bnull\b|-?\d+(\.\d+)?([eE][+\-]?\d+)?)/g,
      (match) => {
        let cls = "text-slate-300";
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = "text-cyan-300";
          } else {
            cls = "text-emerald-300";
          }
        } else if (/true|false/.test(match)) {
          cls = "text-amber-300";
        } else if (/null/.test(match)) {
          cls = "text-slate-500";
        } else {
          cls = "text-fuchsia-300";
        }
        return `<span class="${cls}">${match}</span>`;
      }
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-800 bg-slate-950 p-4">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">User</p>
          <p className="text-white font-bold">{user?.name || "-"}</p>
          <p className="text-xs text-slate-400 mt-1">{user?.user_id || "-"}</p>
          <p className="text-xs text-slate-500 mt-1">Manglik: {user?.manglik?.label || "-"}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-950 p-4">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Candidate</p>
          <p className="text-white font-bold">{candidate?.name || "-"}</p>
          <p className="text-xs text-slate-400 mt-1">{candidate?.user_id || "-"}</p>
          <p className="text-xs text-slate-500 mt-1">
            Work Location: {candidate?.occupation_location || candidate?.occupationLocation || "-"}
          </p>
          <p className="text-xs text-slate-500 mt-1">Manglik: {candidate?.manglik?.label || "-"}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className={`rounded-xl border p-3 ${scoreColor(result?.composite_score || 0)}`}>
          <p className="text-[10px] uppercase tracking-widest opacity-80">Composite</p>
          <p className="text-xl font-black">{(result?.composite_score || 0).toFixed?.(2) ?? result?.composite_score ?? "-"}</p>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-3">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Gun Score</p>
          <p className="text-xl font-black text-amber-400">{gun?.total_gun_score ?? "-"}</p>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-3">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Strict Gun</p>
          <p className="text-xl font-black text-fuchsia-400">{gun?.total_gun_score_strict ?? "-"}</p>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-3">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Result</p>
          <p className={`text-sm font-black mt-1 ${result?.passed ? "text-emerald-400" : "text-rose-400"}`}>
            {result?.passed ? "PASSED" : "FAILED"}
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-950 p-4">
        <p className="text-xs text-slate-300 font-semibold mb-2">Summary</p>
        <p className="text-sm text-slate-300 leading-relaxed">{result?.summary || gun?.interpretation || "No summary available."}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-800 bg-slate-950 p-4">
          <p className="text-xs text-slate-300 font-semibold mb-2">Gun Milan Scores</p>
          <div className="space-y-1.5">
            {Object.keys(gunScores).length === 0 && <p className="text-xs text-slate-500">No data</p>}
            {Object.entries(gunScores).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-xs">
                <span className="text-slate-400">{k}</span>
                <span className="text-amber-300 font-bold">{String(v)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-950 p-4">
          <p className="text-xs text-slate-300 font-semibold mb-2">Strict Scores</p>
          <div className="space-y-1.5">
            {Object.keys(strictScores).length === 0 && <p className="text-xs text-slate-500">No data</p>}
            {Object.entries(strictScores).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-xs">
                <span className="text-slate-400">{k}</span>
                <span className="text-fuchsia-300 font-bold">{String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-xl border border-emerald-700/20 bg-emerald-900/10 p-4">
          <p className="text-xs text-emerald-300 font-semibold mb-2">Strengths</p>
          <div className="space-y-1">
            {strengths.length === 0 && <p className="text-xs text-slate-400">No strengths listed</p>}
            {strengths.map((item, idx) => (
              <p key={idx} className="text-xs text-slate-200">• {item}</p>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-amber-700/20 bg-amber-900/10 p-4">
          <p className="text-xs text-amber-300 font-semibold mb-2">Challenges</p>
          <div className="space-y-1">
            {challenges.length === 0 && <p className="text-xs text-slate-400">No challenges listed</p>}
            {challenges.map((item, idx) => (
              <p key={idx} className="text-xs text-slate-200">• {item}</p>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-950 p-4">
        <p className="text-xs text-slate-300 font-semibold mb-2">Raw JSON</p>
        <pre className="text-[11px] leading-relaxed overflow-auto max-h-[380px] bg-slate-900 border border-slate-800 rounded-lg p-3">
          <code dangerouslySetInnerHTML={{ __html: syntaxHighlightJson(data) }} />
        </pre>
      </div>
    </div>
  );
};

export default AstrologyDetailsView;
